import { createContext, useContext, useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { Home, ShoppingCart, FileText, Package, ClipboardList, Target, Coins, MessageSquare, FolderOpen, GraduationCap, LogOut, Loader2, Menu, X } from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { applySocieteBranding } from "../../hooks/useAppConfig";

const PartnerCtx = createContext(null);
export const usePartner = () => useContext(PartnerCtx);

const NAV = [
  { key: "home", to: "/partner", label: "Accueil", icon: Home, always: true },
  { key: "commandes", to: "/partner/commandes", label: "Commandes", icon: ShoppingCart },
  { key: "factures", to: "/partner/factures", label: "Factures", icon: FileText },
  { key: "catalogue", to: "/partner/catalogue", label: "Catalogue", icon: Package },
  { key: "scorm_import", to: "/partner/formation", label: "Mes formations", icon: GraduationCap, alsoKeys: ["programmes", "sessions", "stagiaires"] },
  { key: "animation", to: "/formateur/sessions", label: "Animer mes sessions", icon: GraduationCap },
  { key: "missions", to: "/partner/missions", label: "Missions", icon: ClipboardList },
  { key: "leads", to: "/partner/leads", label: "Leads apportés", icon: Target },
  { key: "commissions", to: "/partner/commissions", label: "Commissions", icon: Coins },
  { key: "messagerie", to: "/partner/messages", label: "Messagerie", icon: MessageSquare },
  { key: "documents", to: "/partner/documents", label: "Documents", icon: FolderOpen },
];

/** Coque du panel partenaire : branding de la société + navigation limitée aux modules actifs. */
export default function PartnerLayout({ children }) {
  const { axiosAuth, logout } = useAuth();
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [err, setErr] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    axiosAuth().get("/partner/me").then((r) => { setMe(r.data); applySocieteBranding(r.data.branding); }).catch((e) => setErr(e.response?.data?.detail || e.message));
    // eslint-disable-next-line
  }, []);

  if (err) return <div className="min-h-screen flex items-center justify-center bg-[#0b0811] text-slate-300 text-sm" data-testid="partner-error">{err}</div>;
  if (!me) return <div className="min-h-screen flex items-center justify-center bg-[#0b0811]"><Loader2 className="w-8 h-8 animate-spin text-[#f7c935]" /></div>;

  const b = me.branding || {};
  const access = me.access || {};
  const links = NAV.filter((l) => l.always || access[l.key] || (l.alsoKeys || []).some((k) => access[k]));
  const primary = b.primary || "#f7c935";

  return (
    <PartnerCtx.Provider value={me}>
      <div className="min-h-screen flex" style={{ background: b.background || "#0b0811" }} data-testid="partner-layout">
        <aside className={"fixed md:static inset-y-0 left-0 z-40 w-64 flex flex-col border-r border-white/10 transition-transform md:translate-x-0 " + (open ? "translate-x-0" : "-translate-x-full")} style={{ background: b.secondary || "#14101c" }}>
          <div className="px-5 py-5 border-b border-white/10 flex items-center gap-3">
            {b.logo_url ? <img src={b.logo_url} alt={b.app_name} className="h-9 max-w-[140px] object-contain" data-testid="partner-logo" /> : <div className="h-9 px-3 rounded-lg flex items-center text-xs font-black" style={{ background: primary, color: "#14101c" }} data-testid="partner-logo">{b.app_name}</div>}
            <button className="md:hidden ml-auto text-slate-400" onClick={() => setOpen(false)}><X className="w-5 h-5" /></button>
          </div>
          <div className="px-5 py-3 text-[10px] uppercase tracking-widest text-slate-400 font-bold">Panel partenaire · {me.type_label}</div>
          <nav className="flex-1 px-3 space-y-1">
            {links.map((l) => (
              <NavLink key={l.key} to={l.to} end={l.to === "/partner"} onClick={() => setOpen(false)} data-testid={"partner-nav-" + l.key}
                className={({ isActive }) => "flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm font-semibold transition-colors " + (isActive ? "text-[#14101c]" : "text-slate-300 hover:bg-white/5")}
                style={({ isActive }) => (isActive ? { background: primary } : {})}>
                <l.icon className="w-4 h-4" /> {l.label}
              </NavLink>
            ))}
          </nav>
          <div className="px-5 py-4 border-t border-white/10 text-xs text-slate-400">
            <div className="text-white font-bold truncate">{me.partner?.company || me.partner?.name}</div>
            <div className="truncate">{me.partner?.email}</div>
            <button onClick={async () => { await logout(); navigate("/login"); }} className="mt-3 flex items-center gap-1.5 text-slate-400 hover:text-white" data-testid="partner-logout"><LogOut className="w-3.5 h-3.5" /> Déconnexion</button>
            {b.footer_text && <div className="mt-3 text-[10px] text-slate-500">{b.footer_text}</div>}
          </div>
        </aside>
        <main className="flex-1 min-w-0">
          <div className="md:hidden flex items-center gap-3 px-4 py-3 border-b border-white/10">
            <button onClick={() => setOpen(true)} className="text-slate-300"><Menu className="w-5 h-5" /></button>
            <span className="text-white font-bold text-sm">{b.app_name}</span>
          </div>
          <div className={children ? "" : "p-5 md:p-8 max-w-6xl mx-auto"}>{children || <Outlet />}</div>
        </main>
      </div>
    </PartnerCtx.Provider>
  );
}
